import { useEffect, useId, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import Modal from "../../modal/Modal";
import FormSelect from "../../shared/FormSelect";
import { buildCreateBody, buildUpdateBody, validateClientForm } from "./clientForm";
import { withFieldError } from "./formErrors";

const STATUS_OPTIONS = [
    { value: "ACTIVE", label: "Active" },
    { value: "INACTIVE", label: "Inactive" },
];

const statusOptionFor = (status) =>
    STATUS_OPTIONS.find((option) => option.value === status) ?? STATUS_OPTIONS[0];

const EMPTY_FORM = {
    clientId: "",
    displayName: "",
    groupId: "",
    status: "ACTIVE",
};

const ClientFormDialog = ({ mode, client, saving, serverError, onClose, onSubmit }) => {
    const titleId = useId();
    const firstFieldRef = useRef(null);
    const isCreate = mode === "create";
    const [values, setValues] = useState(() =>
        isCreate
            ? EMPTY_FORM
            : {
                  clientId: client?.clientId ?? "",
                  displayName: client?.displayName ?? "",
                  groupId: client?.groupId ?? "",
                  status: client?.status === "INACTIVE" ? "INACTIVE" : "ACTIVE",
              },
    );
    const [errors, setErrors] = useState({});
    const valuesRef = useRef(values);
    valuesRef.current = values;
    const { control, getValues } = useForm({
        defaultValues: {
            status: statusOptionFor(isCreate ? "ACTIVE" : client?.status),
        },
    });

    useEffect(() => {
        firstFieldRef.current?.focus();
    }, []);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === "Escape" && !saving) onClose();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [onClose, saving]);

    const setField = (key) => (event) => {
        setValues((current) => ({ ...current, [key]: event.target.value }));
    };

    const clientErrors = (snapshot) => {
        const nextErrors = validateClientForm(snapshot, { requireClientId: isCreate });
        if (snapshot.status !== "ACTIVE" && snapshot.status !== "INACTIVE") nextErrors.status = "Status is required.";
        return nextErrors;
    };

    const snapshot = (overrides = {}) => ({
        ...valuesRef.current,
        status: getValues("status")?.value,
        ...overrides,
    });

    const reportFieldError = (name, nextSnapshot) => {
        setErrors((current) => withFieldError(current, name, clientErrors(nextSnapshot)[name] || ""));
    };

    const changeText = (name) => (event) => {
        setField(name)(event);
        reportFieldError(name, snapshot({ [name]: event.target.value }));
    };

    const statusValue = useWatch({ control, name: "status" });
    const formInvalid = Object.keys(clientErrors({ ...values, status: statusValue?.value })).length > 0;

    const handleSubmit = (event) => {
        event.preventDefault();
        if (saving) return;
        const nextValues = snapshot();
        const nextErrors = clientErrors(nextValues);
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) return;
        const body = isCreate ? buildCreateBody(nextValues) : buildUpdateBody(nextValues);
        onSubmit(body);
    };

    return (
        <Modal isOpen className="modal-content-compact" onClose={saving ? undefined : onClose}>
            <form className="cdes-client-form" onSubmit={handleSubmit} noValidate>
                <p className="title" id={titleId}>
                    {isCreate ? "Add Client" : "Edit Client"}
                </p>
                <div className="form-input">
                    <label htmlFor="cdes-client-id">
                        Client ID {isCreate ? <span className="danger">*</span> : null}
                    </label>
                    <input
                        id="cdes-client-id"
                        ref={isCreate ? firstFieldRef : undefined}
                        value={values.clientId}
                        onChange={isCreate ? changeText("clientId") : setField("clientId")}
                        disabled={!isCreate || saving}
                        readOnly={!isCreate}
                        aria-required={isCreate || undefined}
                        aria-invalid={Boolean(errors.clientId) || undefined}
                        aria-describedby="cdes-client-id-help"
                        autoComplete="off"
                    />
                    <p id="cdes-client-id-help" className="cdes-client-form__help">
                        System identifier. It cannot be changed later.
                    </p>
                    {errors.clientId ? (
                        <p className="danger cdes-client-form__error" role="alert">
                            {errors.clientId}
                        </p>
                    ) : null}
                </div>
                <div className="form-input">
                    <label htmlFor="cdes-client-name">
                        Client Name <span className="danger">*</span>
                    </label>
                    <input
                        id="cdes-client-name"
                        ref={isCreate ? undefined : firstFieldRef}
                        value={values.displayName}
                        onChange={changeText("displayName")}
                        disabled={saving}
                        aria-required="true"
                        aria-invalid={Boolean(errors.displayName) || undefined}
                        aria-describedby={errors.displayName ? "cdes-client-name-error" : undefined}
                        autoComplete="off"
                    />
                    {errors.displayName ? (
                        <p id="cdes-client-name-error" className="danger cdes-client-form__error" role="alert">
                            {errors.displayName}
                        </p>
                    ) : null}
                </div>
                <div className="form-input">
                    <label htmlFor="cdes-client-group">Group ID</label>
                    <input
                        id="cdes-client-group"
                        value={values.groupId}
                        onChange={setField("groupId")}
                        disabled={saving}
                        autoComplete="off"
                    />
                </div>
                <FormSelect
                    name="status"
                    label="Status"
                    required
                    control={control}
                    options={STATUS_OPTIONS}
                    errors={errors.status ? { status: { message: errors.status } } : {}}
                    placeholder="Status"
                    disabled={saving}
                    onSelectedHandler={(option) => reportFieldError("status", snapshot({ status: option?.value ?? "" }))}
                    onBlurHandler={() => reportFieldError("status", snapshot())}
                />
                {serverError ? (
                    <p className="danger cdes-client-form__error" role="alert">
                        {serverError}
                    </p>
                ) : null}
                <div className="cdes-client-form__actions">
                    <button type="button" className="outline-btn" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button type="submit" className="edit-btn" disabled={saving || formInvalid}>
                        {saving ? (isCreate ? "Creating…" : "Saving…") : isCreate ? "Create Client" : "Save Changes"}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default ClientFormDialog;
